import { useEffect, useState } from "react";

import { reviewSettings, saveReviewSettings, type ReviewSettings } from "@/lib/review";

import { Panel } from "./Setup";

/**
 * Review — the idle timeout (日本-185) and word autoplay (日本-347).
 *
 * **It is a setting rather than an env var because it is load-bearing and it is Alex's to choose.**
 * A sitting suspends this handle's syncing so `↑` can hand a card back — `col.undo()` is Anki's own
 * undo, and a sync clears the backend's undo queue in either direction, so there is no version of
 * this that syncs per answer. The consequence is that a session that never ends holds the whole
 * app's syncing, which is why the hold carries a deadline at all.
 *
 * So this number is three things at once and they must not be allowed to disagree: the server's
 * hold deadline, the client's kick-back timer, and the countdown the reader sees in the final
 * minute. It rides on the position payload for exactly that reason — the browser never holds its
 * own copy of it.
 *
 * `1..120` is enforced by the server and **refused rather than clamped**, so a rejected value comes
 * back as the banner at the top of this screen rather than as a box quietly saying something other
 * than what was typed.
 *
 * **Autoplay is synced rather than device-local**, which is the opposite of review's image mode.
 * That one is about the screen in front of you — how much room there is for pictures — where this is
 * about the deck, and the phone and the desktop should agree about whether a card makes a sound.
 *
 * **The Easy threshold is the third control** (日本-700): a Pass on a review card, revealed within this
 * many seconds on its first showing of the day, is recorded as Easy. Entered in seconds with one
 * decimal, stored in milliseconds, and **blank means off** — which is where it starts. The server
 * owns the bounds and refuses rather than clamps, as it does for the timeout.
 *
 * Each control saves **only itself**: the server reads an absent field as "not supplied" rather than
 * as a reset, so toggling the sound cannot carry a half-typed timeout up with it.
 */
export function ReviewPanel({
  onError,
  onSuccess,
}: {
  onError: (error: unknown) => void;
  onSuccess: () => void;
}) {
  const [settings, setSettings] = useState<ReviewSettings | null>(null);
  const idleMinutes = settings?.idleMinutes ?? null;
  const easyThresholdMs = settings?.easyThresholdMs ?? null;
  const easySeconds =
    easyThresholdMs === null || easyThresholdMs === 0 ? "" : String(easyThresholdMs / 1000);

  useEffect(() => {
    void reviewSettings().then(setSettings).catch(onError);
  }, [onError]);

  const saveAutoplay = async (autoplayWord: boolean) => {
    // Optimistic, unlike the timeout beside it: a checkbox has nothing to refuse and nothing to put
    // back, and a tick that waits on a round trip before moving reads as a control that ignored you.
    setSettings((current) => (current ? { ...current, autoplayWord } : current));
    try {
      setSettings(await saveReviewSettings({ autoplayWord }));
      onSuccess();
    } catch (e) {
      setSettings((current) => (current ? { ...current, autoplayWord: !autoplayWord } : current));
      onError(e);
    }
  };

  const save = async (minutes: number, field: HTMLInputElement) => {
    try {
      const saved = await saveReviewSettings({ idleMinutes: minutes });
      setSettings(saved);
      onSuccess();
    } catch (e) {
      // **A refused value must not stay in the box.** The bounds refuse rather than clamp, so
      // leaving `999` on screen beside a banner explaining why it was rejected leaves the panel
      // stating a timeout the server does not have — and the next blur would not even retry,
      // because the field would already agree with itself. Put back what is really stored.
      field.value = String(idleMinutes ?? "");
      onError(e);
    }
  };

  const saveEasy = async (ms: number, field: HTMLInputElement) => {
    try {
      setSettings(await saveReviewSettings({ easyThresholdMs: ms }));
      onSuccess();
    } catch (e) {
      // The timeout's rule: a refused value does not stay in the box.
      field.value = easySeconds;
      onError(e);
    }
  };

  return (
    <Panel title="復習" subtitle="無操作タイムアウト・音声・簡単の自動判定">
      <div className="flex flex-wrap items-end gap-[14px] px-[18px] py-3">
        <label className="flex flex-col gap-[3px]">
          <span className="text-meta text-ink-3">無操作タイムアウト</span>
          <input
            type="number"
            min={1}
            max={120}
            // `defaultValue` cannot resync after the fact — React ignores later changes to one — so
            // the field is only mounted once the real value has arrived. `key` remounts it if the
            // value moves underneath us, which is what makes a refused save put the box back.
            key={idleMinutes ?? "loading"}
            defaultValue={idleMinutes ?? ""}
            disabled={idleMinutes === null}
            onBlur={(e) => {
              // A blank box is `Number("") === 0` — a real number, and a real refusal from the
              // server. Neither a blank nor a typed decimal is a value the reader chose, so it is
              // put back rather than sent.
              const raw = e.target.value.trim();
              const value = Number(raw);
              if (raw === "" || !Number.isInteger(value)) {
                e.target.value = String(idleMinutes ?? "");
                return;
              }
              if (idleMinutes !== null && value !== idleMinutes) void save(value, e.target);
            }}
            className="w-[110px] rounded-[var(--r-2)] border border-line-strong bg-void px-2 py-[6px] text-[14px]/[1.2] text-ink disabled:opacity-40"
          />
          <span className="text-meta text-ink-4">分・1〜120</span>
        </label>
        <p className="max-w-[46ch] text-meta text-ink-4">
          復習中の解答はその都度送らずサーバに保持します。だから{" "}
          <span className="font-mono">↑</span>{" "}
          {/* One expression, not two wrapped lines: JSX collapses a newline between two CJK
              characters into a SPACE, so this rendered `…デッキ一覧に戻り、 その回の…`. Japanese
              has no inter-word space, so the stray one reads as a typo rather than as wrapping. */}
          {"でカードを取り戻せます。この時間だけキー・タップ・スワイプがないとデッキ一覧に戻り、その回の解答が送信されます。最後の1分でカウントダウンが出ます。"}
        </p>
      </div>
      <div className="flex flex-wrap items-start gap-[14px] border-t border-line-soft px-[18px] py-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings?.autoplayWord ?? false}
            disabled={settings === null}
            onChange={(e) => void saveAutoplay(e.target.checked)}
            className="h-[13px] w-[13px] accent-[var(--accent)] disabled:opacity-40"
          />
          <span className="text-meta text-ink-2">解答表示時に単語を再生</span>
        </label>
        <p className="max-w-[46ch] text-meta text-ink-4">
          単語のみ。文の音声は <span className="font-mono">文</span>{" "}
          ボタンに残るので、後ろに溜まりません。
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-[14px] border-t border-line-soft px-[18px] py-3">
        <label className="flex flex-col gap-[3px]">
          <span className="text-meta text-ink-3">簡単とみなす秒数</span>
          <input
            type="number"
            min={0.5}
            max={10}
            step={0.1}
            inputMode="decimal"
            placeholder="オフ"
            // The timeout's `key` trick, for the timeout's reason: remount when the stored value
            // moves, so a refused save puts the box back.
            key={easyThresholdMs ?? "loading"}
            defaultValue={easySeconds}
            disabled={easyThresholdMs === null}
            data-check="setup.review.easy"
            onBlur={(e) => {
              // A number input reports unparseable text (`1,5`, `abc`) as an EMPTY value, which
              // would otherwise read as blank-is-off and silently switch the threshold off.
              if (e.target.validity.badInput) {
                e.target.value = easySeconds;
                return;
              }
              const raw = e.target.value.trim();
              // Blank is OFF, and off is a value the server stores as 0 — not a box to put back.
              const ms = raw === "" ? 0 : Math.round(Number(raw) * 1000);
              if (!Number.isFinite(ms)) {
                e.target.value = easySeconds;
                return;
              }
              if (easyThresholdMs !== null && ms !== easyThresholdMs) void saveEasy(ms, e.target);
            }}
            className="w-[110px] rounded-[var(--r-2)] border border-line-strong bg-void px-2 py-[6px] text-[14px]/[1.2] text-ink placeholder:text-ink-4 disabled:opacity-40"
          />
          <span className="text-meta text-ink-4">秒・0.5〜10・空欄でオフ</span>
        </label>
        <p className="max-w-[46ch] text-meta text-ink-4">
          {"その日初めての復習カードを、この秒数以内に答えを表示して合格すると「簡単」として記録します。新規・学習中のカードは対象外です。"}
        </p>
      </div>
    </Panel>
  );
}
